import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';

// Ordenação client-side pra data tables do painel. Sempre client-side por
// enquanto — listas são LIMIT 500 no backend, dá pra ordenar em memória sem
// pain. Se alguma tabela crescer > 1k rows, migramos pra server-side (backend
// aceita ?sort=field&order=asc|desc).

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  field: string | null;
  direction: SortDirection;
}

interface SortableColumnProps {
  label: string;
  field: string;
  sort: SortState;
  onSort: (field: string) => void;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

// Renderiza `<th>` clicável com indicador visual de ordenação atual.
export function SortableColumn({ label, field, sort, onSort, align = 'left', className = '' }: SortableColumnProps) {
  const isActive = sort.field === field;
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : ''} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 ${alignClass} text-inherit transition-colors hover:text-text-primary`}
      >
        <span>{label}</span>
        {isActive ? (
          sort.direction === 'asc'
            ? <ChevronUp size={12} className="text-brand" />
            : <ChevronDown size={12} className="text-brand" />
        ) : (
          <ChevronsUpDown size={12} className="opacity-40" />
        )}
      </button>
    </th>
  );
}

// Hook stateful pra ordenar arrays. Retorna array ordenado + estado + setter.
// Toggle direction quando clica no mesmo field; nova coluna sempre entra desc.
//
// `getValue` (opcional) permite ordenar por campos derivados/aninhados sem
// duplicar as rows.
export function useTableSort<T extends Record<string, any>>(
  rows: T[] | undefined,
  defaultField: string | null = null,
  defaultDirection: SortDirection = 'desc',
  getValue?: (row: T, field: string) => unknown,
) {
  const [sort, setSort] = useState<SortState>({ field: defaultField, direction: defaultDirection });

  const handleSort = (field: string) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'desc' };
    });
  };

  const sorted = useMemo(() => {
    if (!rows || rows.length === 0 || !sort.field) return rows ?? [];
    const arr = [...rows];
    const field = sort.field;
    const dir = sort.direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = getValue ? getValue(a, field) : a[field];
      const vb = getValue ? getValue(b, field) : b[field];
      // Nulls sempre no final independente da direção
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [rows, sort, getValue]);

  return { sorted, sort, handleSort };
}
